import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { API_ENDPOINT, ERROR_MESSAGE, LOCAL_STORAGE_KEY, MODULE, ROUTER_CONFIG } from '@core/configs';
import { Role } from '@core/configs/roles.config';
import { CKPassLoginModel } from '@core/models/interfaces/login.interface';
import { UserType } from '@core/models/enums';
import { ApiResponse, Auth } from '@core/models/interfaces';
import {
  ApiService,
  AutoLogoutService,
  HttpCancelService,
  LocalStorageService,
  RequestService,
} from '@core/services/common';
import { BehaviorSubject, Observable, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  jwtHelperService: JwtHelperService = new JwtHelperService();
  private isLoggedIn = new BehaviorSubject<boolean>(false);
  constructor(
    private readonly http: ApiService,
    private router: Router,
    private autoLogoutService: AutoLogoutService,
    private cancelService: HttpCancelService,
    private requestService: RequestService,
    private localStorageService: LocalStorageService,
  ) {
    if (this.loggedIn()) {
      this.setIsLoggedIn(true);
    }
   }
  get type(): UserType {
    const userSession = this.localStorageService.getUserSession();
    if (userSession && userSession.accessToken) {
      const decode = this.jwtHelperService.decodeToken(userSession.accessToken);
      return decode.type;
    }
    return null;
  }

  get isAccessTokenExpired(): boolean {
    const accessToken = this.localStorageService.getAccessToken();
    if (accessToken) {
      return this.jwtHelperService.isTokenExpired(accessToken);
    }
    return true;
  }

  get isRefreshTokenExpired(): boolean {
    const refreshToken = this.localStorageService.getRefreshToken();
    if (refreshToken) {
      return this.jwtHelperService.isTokenExpired(refreshToken);
    }
    return true;
  }

  setIsLoggedIn(value: boolean) {
    this.isLoggedIn.next(value);
  }

  getIsLoggedIn() {
    return this.isLoggedIn.asObservable();
  }

  loggedIn() {
    if (this.isRefreshTokenExpired) {
      if (this.localStorageService.getItem(LOCAL_STORAGE_KEY.userSession)) {
        this.logout();
      }
      return false;
    } else {
      return true;
    }
  }

  getModuleByToken(token: string): string {
    const decode = this.jwtHelperService.decodeToken(token);
    return decode.module;
  }

  async loginByCKPass(loginData: CKPassLoginModel) {
    return this.http.post(API_ENDPOINT.auth.loginByCKPass, loginData).toPromise();
  }

  requestAccessToken(refreshToken: string): Observable<ApiResponse<any>> {
    if (refreshToken) {
      return this.http.post(API_ENDPOINT.token.refreshToken, {
        refreshToken,
      });
    } else {
      return throwError(ERROR_MESSAGE.refreshTokenNotFound);
    }
  }

  async logout() {
    if (!this.requestService.checkCompletedRequests()) {
      this.cancelService.cancelPendingRequest();
    }
    if (!this.isAccessTokenExpired || !this.isRefreshTokenExpired) {
      this.callLogout()
        .toPromise()
        .catch(() => {
          this.callLogout();
        });
    }
    this.autoLogoutService.stop();
    this.localStorageService.clear();
    sessionStorage.clear();
    this.localStorageService.trigger(LOCAL_STORAGE_KEY.removeToken);
    this.setIsLoggedIn(false);
    return this.router.navigate([ROUTER_CONFIG.auth.login]);
  }

  verifyRefreshToken(): Observable<any> {
    const refreshToken = this.localStorageService.getRefreshToken();
    if (refreshToken) {
        return this.http.post(API_ENDPOINT.auth.verifyRefreshToken, {
            refreshToken,
        });
    } else {
        // tslint:disable-next-line:no-console
        console.error('REFRESH_TOKEN_NOT_FOUND');
        return throwError('REFRESH_TOKEN_NOT_FOUND');
    }
  }

  private callLogout() {
    return this.http.delete(API_ENDPOINT.token.logout);
  }
}
